import { BoundingBox, HandleType, ROTATION_HANDLE_OFFSET, TransformHandle } from "./types";

/**
 * Draw the bounding box around selected shapes
 */
export function drawBoundingBox(
  ctx: CanvasRenderingContext2D,
  bounds: BoundingBox,
  zoomLevel: number
): void {
  ctx.save();

  // Dashed blue border
  ctx.strokeStyle = "#2196F3";
  ctx.lineWidth = 1 / zoomLevel;
  ctx.setLineDash([4 / zoomLevel, 4 / zoomLevel]);

  ctx.strokeRect(bounds.minX, bounds.minY, bounds.width, bounds.height);

  ctx.restore();
}

/**
 * Draw a corner or edge handle
 */
export function drawHandle(
  ctx: CanvasRenderingContext2D,
  handle: TransformHandle,
  isHovered: boolean,
  zoomLevel: number
): void {
  ctx.save();

  const { x, y, width, height } = handle;

  // White fill with blue border
  ctx.fillStyle = isHovered ? "#BBDEFB" : "#FFFFFF";
  ctx.strokeStyle = "#2196F3";
  ctx.lineWidth = 1 / zoomLevel;
  ctx.setLineDash([]);

  ctx.fillRect(x, y, width, height);
  ctx.strokeRect(x, y, width, height);

  ctx.restore();
}

/**
 * Draw the rotation handle (circle with stem)
 */
export function drawRotationHandle(
  ctx: CanvasRenderingContext2D,
  handle: TransformHandle,
  bounds: BoundingBox,
  isHovered: boolean,
  zoomLevel: number
): void {
  ctx.save();

  const centerX = handle.x + handle.width / 2;
  const centerY = handle.y + handle.height / 2;
  const radius = handle.width / 2;

  // Draw stem from bounding box to handle
  ctx.strokeStyle = "#2196F3";
  ctx.lineWidth = 1 / zoomLevel;
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.moveTo(bounds.centerX, bounds.minY);
  ctx.lineTo(centerX, centerY + radius);
  ctx.stroke();

  // Draw circle
  ctx.fillStyle = isHovered ? "#BBDEFB" : "#FFFFFF";
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Draw rotation icon (curved arrow)
  if (radius > 3 / zoomLevel) {
    ctx.strokeStyle = "#2196F3";
    ctx.lineWidth = 1 / zoomLevel;

    const iconRadius = radius * 0.5;
    ctx.beginPath();
    ctx.arc(centerX, centerY, iconRadius, -Math.PI * 0.75, Math.PI * 0.25);
    ctx.stroke();

    // Arrow head
    const arrowAngle = Math.PI * 0.25;
    const arrowX = centerX + iconRadius * Math.cos(arrowAngle);
    const arrowY = centerY + iconRadius * Math.sin(arrowAngle);
    const arrowSize = 2 / zoomLevel;

    ctx.beginPath();
    ctx.moveTo(arrowX - arrowSize, arrowY - arrowSize);
    ctx.lineTo(arrowX, arrowY);
    ctx.lineTo(arrowX + arrowSize, arrowY - arrowSize);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Draw all transform handles for a selection
 */
export function drawTransformOverlay(
  ctx: CanvasRenderingContext2D,
  bounds: BoundingBox,
  handles: TransformHandle[],
  hoveredHandle: HandleType | null,
  zoomLevel: number
): void {
  // Draw bounding box
  drawBoundingBox(ctx, bounds, zoomLevel);

  // Draw handles
  for (const handle of handles) {
    const isHovered = handle.type === hoveredHandle;

    if (handle.type === "rotate") {
      drawRotationHandle(ctx, handle, bounds, isHovered, zoomLevel);
    } else {
      drawHandle(ctx, handle, isHovered, zoomLevel);
    }
  }
}
